import React from 'react';
import { FiHelpCircle, FiX } from 'react-icons/fi';

interface StatusTransitionHelpModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const StatusTransitionHelpModal: React.FC<StatusTransitionHelpModalProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[80vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <FiHelpCircle className="text-blue-500" />
              Project Status Transition Rules
            </h2>
            <button
              onClick={onClose}
              className="p-1.5 rounded-full hover:bg-gray-100 transition-colors"
            >
              <FiX className="w-6 h-6 text-gray-600" />
            </button>
          </div>

          <div className="text-sm text-gray-600">
            <p className="mb-4">
              Project statuses follow specific workflow transition rules to ensure the proper flow of work.
              Here are the rules that determine which status transitions are allowed:
            </p>

            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="border rounded-md p-4 bg-blue-50 border-blue-100">
                <h3 className="font-bold text-blue-800 mb-2">New Projects</h3>
                <p>New projects can be assigned any status.</p>
              </div>

              <div className="border rounded-md p-4 bg-red-50 border-red-100">
                <h3 className="font-bold text-red-800 mb-2">CANCELLED Projects</h3>
                <p>Once a project is CANCELLED, its status cannot be changed (admin override required).</p>
              </div>
            </div>

            <h3 className="font-medium text-gray-800 mb-2">Specific Transition Rules:</h3>

            <table className="min-w-full border-collapse mb-6">
              <thead>
                <tr className="bg-gray-100">
                  <th className="py-2 px-4 text-left border">Current Status</th>
                  <th className="py-2 px-4 text-left border">Allowed Next Status</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-2 px-4 border">BILLED</td>
                  <td className="py-2 px-4 border">Can only come from DELIVERED</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border">HOLD</td>
                  <td className="py-2 px-4 border">Can only be set from ASSIGNED, IN_PROGRESS, or REVIEW</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border">From HOLD</td>
                  <td className="py-2 px-4 border">Can go to ASSIGNED, IN_PROGRESS, REVIEW, DELIVERED, or BILLED</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border">CANCELLED</td>
                  <td className="py-2 px-4 border">Can be set from ASSIGNED, IN_PROGRESS, REVIEW, DELIVERED, or HOLD</td>
                </tr>
                <tr>
                  <td className="py-2 px-4 border">REOPEN</td>
                  <td className="py-2 px-4 border">
                    Can only be set from DELIVERED, BILLED, or PAYMENT_RECEIVED
                  </td>
                </tr>
              </tbody>
            </table>

            <div className="bg-yellow-50 border border-yellow-200 rounded p-4 mb-4">
              <h3 className="font-medium text-yellow-800 mb-1">REOPEN Status Note:</h3>
              <p className="text-yellow-800">
                When a project is reopened with the REOPEN status:
              </p>
              <ul className="list-disc pl-5 text-yellow-800 mt-1">
                <li>If marked as chargeable, a new project will be created</li>
                <li>If not chargeable, status will automatically be changed to ASSIGNED</li>
              </ul>
            </div>
          </div>

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              Got it
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusTransitionHelpModal;
